import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { templateService } from '@/services/templateService';
import { Skeleton } from '@/components/ui/skeleton';
import type { VideoTemplate } from '@/types/template';

interface TemplateSelectorProps {
  selectedTemplateId?: string;
  onSelect: (templateId: string) => void;
}

export function TemplateSelector({ selectedTemplateId, onSelect }: TemplateSelectorProps) {
  const [templates, setTemplates] = useState<VideoTemplate[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadTemplates();
  }, []);

  const loadTemplates = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await templateService.getTemplates();
      setTemplates(response.templates);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar las plantillas');
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2].map((i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-sm text-red-700">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4">
        {templates.map(template => (
          <div
            key={template.id}
            className={cn(
              'border rounded-md p-4 cursor-pointer transition-all',
              selectedTemplateId === template.id
                ? 'border-gray-900 bg-gray-50'
                : 'border-gray-200 hover:border-gray-400 hover:bg-gray-50'
            )}
            onClick={() => onSelect(template.id)}
          >
            <div className="flex justify-between items-start mb-2">
              <h4 className="font-medium text-gray-900">{template.name}</h4>
              {selectedTemplateId === template.id && (
                <span className="text-xs font-medium text-gray-900 bg-gray-200 px-2 py-1 rounded">
                  Activa
                </span>
              )}
            </div>
            
            <p className="text-sm font-light text-gray-600 mb-2">{template.description}</p>
            
            {template.preview_url && (
              <div className="mt-2">
                <video
                  src={template.preview_url}
                  className="w-full h-32 object-cover rounded border border-gray-200"
                  muted
                  loop
                  playsInline
                  onMouseEnter={(e) => e.currentTarget.play()}
                  onMouseLeave={(e) => {
                    e.currentTarget.pause();
                    e.currentTarget.currentTime = 0;
                  }}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
